/** Human-readable rendering of wire values. Pure; shared by the pages and the tests. */

import { BP_CLASS_LABEL, SYMPTOM_LABEL, classifyBp } from '@heartcoach/care';
import type { BpClass, BpReading } from './types';

export function fmtCoins(n: number): string {
  return n.toLocaleString('en-US');
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function shortAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 10)}…${address.slice(-8)}` : address;
}

const ADDRESS_LIKE = /^0x[0-9a-fA-F]{4,}(…|\.{3})?[0-9a-fA-F]*$/;

export function looksLikeAddress(value: string | null | undefined): boolean {
  const v = value?.trim() ?? '';
  return v !== '' && ADDRESS_LIKE.test(v);
}

export function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0 || looksLikeAddress(name)) return '♥';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** "Good morning" / "Good afternoon" / "Good evening" for a local hour. */
export function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function localHour(now: number, tzOffsetMinutes: number): number {
  return new Date(now + tzOffsetMinutes * 60_000).getUTCHours();
}

export function fmtBp(r: { systolic: number; diastolic: number } | null | undefined): string {
  return r ? `${r.systolic}/${r.diastolic}` : '—';
}

export { SYMPTOM_LABEL, classifyBp };

const TONE: Record<BpClass, 'good' | 'warn' | 'bad'> = { low: 'warn', optimal: 'good', elevated: 'warn', 'stage-1': 'warn', 'stage-2': 'bad', crisis: 'bad' };

export const BP_CLASS: Record<BpClass, { label: string; tone: 'good' | 'warn' | 'bad' }> = Object.fromEntries(
  (Object.keys(BP_CLASS_LABEL) as BpClass[]).map((k) => [k, { label: BP_CLASS_LABEL[k], tone: TONE[k] }]),
) as Record<BpClass, { label: string; tone: 'good' | 'warn' | 'bad' }>;

/** "8:00 AM" from "08:00". */
export function fmtClock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "Once daily · 8:00 AM" / "Twice daily · 8AM & 6PM". */
export function fmtSchedule(times: readonly string[]): string {
  const words = ['Once', 'Twice', 'Three times', 'Four times', 'Five times', 'Six times'];
  const how = `${words[times.length - 1] ?? `${times.length} times`} daily`;
  if (times.length === 1) return `${how} · ${fmtClock(times[0] as string)}`;
  return `${how} · ${times.map((t) => fmtClock(t).replace(':00 ', '')).join(' & ')}`;
}

/** "2 min ago", "3 h ago", "yesterday", "Sep 4". */
export function fmtAgo(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function fmtDate(at: number | string): string {
  const d = typeof at === 'string' ? new Date(`${at}T12:00:00Z`) : new Date(at);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function fmtDateTime(at: number): string {
  return new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function describeReading(r: BpReading): string {
  const cls = BP_CLASS[classifyBp(r.systolic, r.diastolic)]?.label ?? '';
  const symptoms = r.symptoms.filter((s) => s !== 'none').map((s) => SYMPTOM_LABEL[s]);
  return `${r.systolic}/${r.diastolic} · ${cls}${symptoms.length ? ` · ${symptoms.join(', ')}` : ''}`;
}

/** "12" → the fire badge text; `0` shows nothing. */
export function streakBadge(n: number): string | null {
  return n > 0 ? String(n) : null;
}

export function pct(n: number): string {
  return `${Math.round(n)}%`;
}
